import { Disclosure } from "@bared/ui";
import { Breadcrumbs, Link } from "@bared/ui";
import { FilterSectionProductStats } from "@components/Filter/FilterSectionProductStats";
import { FilterSectionSort } from "@components/Filter/FilterSectionSort";
import CollectionHeroBanner from "@components/Section/CollectionHeroBanner/CollectionHeroBanner";
import { SectionsProps } from "@components/Sections/withSections";
import { FilterContext } from "~/providers/FilterProvider";
import clsx from "clsx";
import React, { useContext } from "react";

export type CollectionHeaderProps = {
  collectionHeroBannerContent?: {
    bannerTextColor?: "lightest" | "primary";
  } & SectionsProps;
  description?: string;
  paths: {
    title?: string;
    url?: string;
  }[];
  title: string;
  hideBreadcrumbAndTitle: boolean;
};

const Component = ({
  hideBreadcrumbAndTitle,
  title,
  collectionHeroBannerContent,
  paths,
}: CollectionHeaderProps) => {
  return (
    <CollectionHeroBanner
      {...collectionHeroBannerContent}
      collectionTitle={title}
    >
      {!hideBreadcrumbAndTitle && !!paths?.length && (
        <Breadcrumbs
          className={clsx(
            "absolute left-midi top-midi z-10 md:top-maxi xl:left-maxi",
            collectionHeroBannerContent?.bannerTextColor === "lightest" &&
              "text-white [&_a]:text-white [&_svg]:text-white",
          )}
          data-testid={"page-breadcrumb"}
        >
          {paths
            .map((path) => {
              if (!path) return;
              return path.url ? (
                <Link
                  href={path.url}
                  variant="link"
                  className={clsx(
                    collectionHeroBannerContent?.bannerTextColor ===
                      "lightest" && "hover:text-white",
                  )}
                >
                  {path.title}
                </Link>
              ) : (
                <span>{path.title}</span>
              );
            })
            .filter(Boolean)}
        </Breadcrumbs>
      )}
      <h1
        className="pt-tera text-left font-display text-[32px] leading-h1 xl:text-[56px] xl:leading-h4"
        data-testid="collection-banner-page-title"
      >
        {!hideBreadcrumbAndTitle ? title : ""}
      </h1>
    </CollectionHeroBanner>
  );
};

export const CollectionHeader = ({
  description,
  paths,
  title,
  hideBreadcrumbAndTitle,
  collectionHeroBannerContent,
}: CollectionHeaderProps) => {
  const { sortOption, sortOptions, setSortOption } = useContext(FilterContext);

  return (
    <div
      className={clsx(
        !collectionHeroBannerContent
          ? "relative mx-auto px-midi pt-tera"
          : "mx-zero px-zero",
        "relative flex flex-col",
      )}
    >
      {!collectionHeroBannerContent && !!paths?.length && (
        <Breadcrumbs
          className="absolute left-midi top-midi z-10 md:top-maxi xl:left-maxi"
          data-testid={"page-breadcrumb"}
        >
          {paths
            .map((path) => {
              if (!path) return;
              return path.url ? (
                <Link href={path.url} variant="link">
                  {path.title}
                </Link>
              ) : (
                <span>{path.title}</span>
              );
            })
            .filter(Boolean)}
        </Breadcrumbs>
      )}

      <div
        className={clsx(
          !description && !collectionHeroBannerContent
            ? "flex flex-col justify-between"
            : null,
        )}
      >
        {!collectionHeroBannerContent ? (
          <h1
            className="legacy-xl:text-[56px] text-left font-display text-[32px] leading-h1 xl:text-[56px] xl:leading-h4"
            data-testid="page-title"
          >
            {title}
          </h1>
        ) : (
          <Component
            hideBreadcrumbAndTitle={hideBreadcrumbAndTitle}
            title={title}
            collectionHeroBannerContent={collectionHeroBannerContent}
            paths={paths}
          />
        )}

        <div
          className={clsx(
            !collectionHeroBannerContent
              ? !description
                ? "w-[auto] flex-col md:flex-row md:items-end md:justify-end"
                : "w-[auto] flex-col justify-between md:flex-row md:items-end"
              : !description
                ? "mx-auto w-full lg:mt-midi lg:justify-end lg:px-midi"
                : "mx-auto w-full flex-col justify-between px-midi",
            "flex",
          )}
        >
          {description ? (
            <div
              data-testid="collection-description"
              className={clsx(
                "mt-midi flex flex-col items-start bg-neutral md:w-[700px]",
                description && !collectionHeroBannerContent && "md:pb-midi",
              )}
            >
              <Disclosure>{description}</Disclosure>
            </div>
          ) : null}
          <div className="before:bottom-0 md:p-0 relative flex items-center justify-between py-[20px] before:absolute before:left-[-24px] before:right-[-24px] before:h-[1px] before:bg-primary-400 md:justify-end md:before:hidden">
            <div className="mr-maxi flex-none self-center text-right">
              <FilterSectionProductStats />
            </div>
            <div className="text-right">
              <FilterSectionSort
                sortOption={sortOption}
                sortOptions={sortOptions}
                setSortOption={setSortOption}
                scrollTo={() => {}}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionHeader;
